import { weekdayShort } from "@repo/client/format";
import {
  "profile-editors.reminder_custom_hint" as customHint,
  "profile-editors.reminder_custom" as customLabel,
  "profile-editors.reminder_days" as daysLabel,
  "profile-editors.reminder_every_hint" as everyHint,
  "profile-editors.reminder_every" as everyLabel,
  "profile-editors.reminder_label" as timeLabel,
  "profile-editors.reminder_training_hint" as trainingHint,
  "profile-editors.reminder_training" as trainingLabel,
} from "@repo/i18n";
import { getLocale } from "@repo/i18n/runtime";
import { useCallback } from "react";
import { ChoiceList, MultiChips } from "../../../ui/controls";
import { BigNumberForm } from "./big-number-form";
import type { LabelledOption } from "./options";
import { FieldError, FieldLabel } from "./parts";
import {
  clockOf,
  REMINDER_MAX_MINUTE,
  REMINDER_MIN_MINUTE,
  REMINDER_STEP_MINUTES,
  type ReminderDays,
  type ReminderForm as ReminderDraft,
} from "./reminder-model";
import { DISPLAY_WEEK } from "./routine-helpers";

export interface ReminderFormProps {
  /** Shown under the day chips: the contract refuses an empty week. */
  daysError?: string;
  onChange: (value: ReminderDraft) => void;
  /** The training config's fixed days; "on training days" is offered only
   *  when there are some. */
  trainingDays: readonly number[];
  value: ReminderDraft;
}

/**
 * A2 `ReminderSheet` (editors.tsx:857-906): the hour on the big-number dial,
 * a quarter hour per step, then the days — every day, the training days, or
 * days the person picks (`reminderWeekdays`, `0` Sunday, LOCAL). The UTC
 * conversion is `reminder-model.ts`'s, at save.
 */
export function ReminderForm({
  value,
  onChange,
  trainingDays,
  daysError,
}: ReminderFormProps) {
  const locale = getLocale();
  const setMinute = useCallback(
    (localMinute: number) => onChange({ ...value, localMinute }),
    [onChange, value]
  );
  const setDays = useCallback(
    (days: ReminderDays) => onChange({ ...value, days }),
    [onChange, value]
  );
  const setCustom = useCallback(
    (custom: string[]) => onChange({ ...value, custom: custom.map(Number) }),
    [onChange, value]
  );
  const options: LabelledOption<ReminderDays>[] = [
    { description: everyHint(), label: everyLabel(), value: "every" },
    ...(trainingDays.length > 0
      ? [
          {
            description: trainingHint(),
            label: trainingLabel(),
            value: "training" as const,
          },
        ]
      : []),
    { description: customHint(), label: customLabel(), value: "custom" },
  ];
  return (
    <div>
      <BigNumberForm
        format={clockOf}
        label={timeLabel()}
        max={REMINDER_MAX_MINUTE}
        min={REMINDER_MIN_MINUTE}
        onChange={setMinute}
        previous={null}
        step={REMINDER_STEP_MINUTES}
        unit=""
        value={value.localMinute}
      />
      <div className="mt-6">
        <FieldLabel>{daysLabel()}</FieldLabel>
        <ChoiceList
          label={daysLabel()}
          onValueChange={setDays}
          options={options}
          value={value.days}
        />
      </div>
      {value.days === "custom" ? (
        <div className="mt-4">
          <MultiChips
            label={customLabel()}
            onValueChange={setCustom}
            options={DISPLAY_WEEK.map((day) => ({
              label: weekdayShort(day, locale),
              value: String(day),
            }))}
            value={value.custom.map(String)}
          />
        </div>
      ) : null}
      {daysError ? <FieldError>{daysError}</FieldError> : null}
    </div>
  );
}
